import { type SearchSchemaInput, createFileRoute, redirect, useNavigate } from '@tanstack/react-router'
import i18next from 'i18next'

import {
  settingsCheckAppEmbedQueryOptions,
  reportsBackordersQueryOptions,
  reportsBisListQueryOptions,
  campaignBySellingPlanIdQueryOptions,
  campaignReportsOrdersQueryOptions,
  settingsQueryOptions,
  reportsUsageQueryOptions,
  campaignQueryOptions,
  reportsBisQueryOptions,
} from '@/common/queryOptions'
import dayjs from '@/common/datetime'

import { PageFrame } from '@/components/shared/PageFrame'
import { UsageStats } from '@/components/shared/UsageStats'
import { CampaignMetrics } from '@/components/dashboard/CampaignMetrics'
import { ThingsToDo } from '@/components/dashboard/ThingsToDo'
import { LegacyThemeBanner } from '@/components/dashboard/LegacyThemeBanner'
import {
  BlockStack,
  Box,
  Button,
  Card,
  Grid,
  InlineGrid,
  InlineStack,
  Layout,
  SkeletonBodyText,
  SkeletonDisplayText,
  SkeletonPage,
  Spinner,
  Text,
} from '@shopify/polaris'
import { SectionTitle } from '@/components/shared/SectionTitle'
import { Onboarding, OnboardingFallback } from '@/components/dashboard/Onboarding'
import { useSuspenseQuery, useSuspenseQueries } from '@tanstack/react-query'
import {AppEmbedStatus} from "@/components/dashboard/AppEmbedStatus.tsx";

export const Route = createFileRoute('/')({
  validateSearch: (search: { sellingPlanGroupId?: string } & SearchSchemaInput) => {
    // validate and parse the search params into a typed state
    return {
      sellingPlanGroupId: search?.sellingPlanGroupId,
    }
  },
  beforeLoad: async ({ search }) => {
    return {
      search,
    }
  },
  loader: async ({ context: { queryClient, search }, route: { path } }) => {
    try {
      if (search.sellingPlanGroupId) {
        const campaign = await queryClient.ensureQueryData(campaignBySellingPlanIdQueryOptions({ sellingPlanGroupId: search.sellingPlanGroupId }))
        throw redirect({
          to: '/preorders/$uuid/settings',
          params: {
            uuid: campaign.uuid,
          },
        })
      }

      await Promise.all([
        queryClient.ensureQueryData(settingsQueryOptions()),
        queryClient.ensureQueryData(reportsUsageQueryOptions()),
        queryClient.ensureQueryData(
          campaignReportsOrdersQueryOptions({
            from: undefined,
            to: undefined,
          })
        ),
        queryClient.ensureQueryData(
          reportsBackordersQueryOptions({
            from: undefined,
            to: undefined,
          })
        ),
        queryClient.ensureQueryData(
          reportsBisQueryOptions({
            from: undefined,
            to: undefined,
          })
        ),
        queryClient.ensureQueryData(
          reportsBisListQueryOptions({
            from: undefined,
            to: undefined,
          })
        ),
        queryClient.ensureQueryData(campaignQueryOptions()),
        queryClient.ensureQueryData(settingsCheckAppEmbedQueryOptions()),
      ])
    } catch (error) {
      console.error('Error in page loader.', path, error)
      throw error
    }
  },
  pendingComponent: Pending,
  component: Page,
})

function Page() {
  const [{ data: settings }, { data: hasEmbedBlock }] = useSuspenseQueries({queries: [settingsQueryOptions(), settingsCheckAppEmbedQueryOptions()]})

  const navigate = useNavigate()

  return (
    <PageFrame
      title={i18next.t('dashTitle')}
      subtitle={i18next.t('dashSubtitle')}
      primaryAction={
        <Button
          variant="secondary"
          onClick={() => {
            navigate({ to: '/preorders/create' })
          }}
        >
          {i18next.t('campaignsCta')}
        </Button>
      }
    >
      <LegacyThemeBanner />
      <Onboarding />

      {settings?.onboarded === true || hasEmbedBlock ? <AppEmbedStatus /> : null }

      <UsageStats />
      <CampaignMetrics />
      <ThingsToDo />
    </PageFrame>
  )
}

function Pending() {
  const { data: settings } = useSuspenseQuery(settingsQueryOptions())

  return (
    <SkeletonPage title={i18next.t('dashTitle')}>
      <Layout>
        {settings?.onboarded === false ? <OnboardingFallback /> : null}
        {settings?.onboarded === true ?
          <Layout.Section>
            <Card>
              <BlockStack gap="200">
                <SkeletonBodyText />
                <SkeletonDisplayText />
              </BlockStack>
            </Card>
          </Layout.Section>
        : null }
        <Layout.Section>
          <SectionTitle
            title={i18next.t('usageStatsTitle')}
            description={i18next.t('usageStatsSubtitle', {
              from: dayjs.utc().startOf('month').format('MMMM D'),
              to: dayjs.utc().endOf('month').format('MMMM D'),
            })}
          />
          <Card padding="400">
            <BlockStack gap="400" align="center" inlineAlign="stretch">
              <Grid
                columns={{
                  xs: 4,
                  sm: 4,
                  md: 4,
                  lg: 4,
                  xl: 4,
                }}
              >
                <Grid.Cell
                  columnSpan={{
                    xs: 1,
                    sm: 1,
                    md: 1,
                    lg: 1,
                    xl: 1,
                  }}
                >
                  <BlockStack gap="200">
                    <SkeletonDisplayText size="small" />
                    <SkeletonBodyText lines={2} />
                  </BlockStack>
                </Grid.Cell>

                <Grid.Cell
                  columnSpan={{
                    xs: 3,
                    sm: 3,
                    md: 3,
                    lg: 3,
                    xl: 3,
                  }}
                >
                  <Box width="100%">
                    <InlineGrid columns={2} gap="400">
                      <BlockStack gap="200">
                        <SkeletonDisplayText size="small" />
                        <SkeletonBodyText lines={2} />
                      </BlockStack>
                      <BlockStack gap="200">
                        <SkeletonDisplayText size="small" />
                        <SkeletonBodyText lines={2} />
                      </BlockStack>
                    </InlineGrid>
                  </Box>
                </Grid.Cell>
              </Grid>
            </BlockStack>
          </Card>
        </Layout.Section>
        <Layout.Section>
          <SectionTitle title={i18next.t('keyMetricsTitle')} description={i18next.t('keyMetricsSubtitle')} />
          <InlineGrid
            gap="400"
            columns={{
              xs: 1,
              sm: 2,
              md: 2,
              lg: 4,
            }}
          >
            <Card>
              <Text as="h3" variant="bodyMd" fontWeight="medium">
                {i18next.t('keyMetricsBackorderRevenueTitle')}
              </Text>
              <SkeletonDisplayText size="large" />
              <Box paddingBlockStart="400" paddingBlockEnd="100">
                <InlineStack align="center" blockAlign="center">
                  <Box>
                    <Spinner size="small" />
                  </Box>
                </InlineStack>
              </Box>
            </Card>

            <Card>
              <Text as="h3" variant="bodyMd" fontWeight="medium">
                {i18next.t('keyMetricsPreorderRevenueTitle')}
              </Text>
              <SkeletonDisplayText size="large" />
              <Box paddingBlockStart="400" paddingBlockEnd="100">
                <InlineStack align="center" blockAlign="center">
                  <Box>
                    <Spinner size="small" />
                  </Box>
                </InlineStack>
              </Box>
            </Card>
            <Card>
              <Text as="h3" variant="bodyMd" fontWeight="medium">
                {i18next.t('keyMetricsBackInStockRevenueTitle')}
              </Text>
              <SkeletonDisplayText size="large" />
              <Box paddingBlockStart="400" paddingBlockEnd="100">
                <InlineStack align="center" blockAlign="center">
                  <Box>
                    <Spinner size="small" />
                  </Box>
                </InlineStack>
              </Box>
            </Card>
            <Card>
              <Text as="h3" variant="bodyMd" fontWeight="medium">
                {i18next.t('keyMetricsBackInStockTitle')}
              </Text>
              <SkeletonDisplayText size="large" />
              <Box paddingBlockStart="400" paddingBlockEnd="100">
                <InlineStack align="center" blockAlign="center">
                  <Box>
                    <Spinner size="small" />
                  </Box>
                </InlineStack>
              </Box>
            </Card>
          </InlineGrid>
        </Layout.Section>
        <Layout.Section>
          <BlockStack gap="300">
            <Card padding="400">
              <BlockStack gap="300">
                <SkeletonDisplayText size="large" />
                <SkeletonBodyText lines={3} />
              </BlockStack>
            </Card>
            <Card padding="400">
              <BlockStack gap="300">
                <SkeletonDisplayText size="large" />
                <SkeletonBodyText lines={3} />
              </BlockStack>
            </Card>
            <Card padding="400">
              <BlockStack gap="300">
                <SkeletonDisplayText size="large" />
                <SkeletonBodyText lines={3} />
              </BlockStack>
            </Card>
          </BlockStack>
        </Layout.Section>
      </Layout>
    </SkeletonPage>
  )
}